import { Fragment, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  DEAL_LINE_ITEM_TYPES,
  addMonthsWire,
  scenarioFigures,
  scenarioMonths,
  type DealLineItemType,
  type DealScenarioDetail,
  type PlaceholderProfile,
  type ScenarioLine,
  type ScenarioResource,
} from '@forecastify/shared';
import {
  Button,
  DropdownMenu,
  Icon,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  cn,
  type MenuItem,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useDuration } from '../../duration';
import { dealMoney, dealMoneyShort, lineTypeLabel, seniorityLabel } from './deal-kit';
import { PlanConfirm, parseAmount } from './plan-kit';
import { ScenarioResourceDialog } from './scenario-dialogs';
import {
  amountField,
  monthLabel,
  monthParts,
  pctText,
  scenarioInput,
  type ScenarioSend,
} from './scenario-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D9 · the scenario's grid — `07b`, `07f`.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Line items as group rows (name, type, hours, revenue, cost, margin) with their resources under
 * them, and a column per month — or, on a recurring scenario, ONE column: the period template,
 * whose cells carry a null period (DS-2).
 *
 * ⚠️⚠️ **A RESOURCE BELONGS TO THE SCENARIO, NOT TO A LINE ITEM** — the same shape as the plan's
 * placeholders (`deal-resource-tab.tsx`). A line shows the resources that HAVE hours on it, plus
 * any put there from its *+ Erőforrás* menu this visit; the server stores hours, not an empty
 * pairing, so a row nobody typed into is gone after a reload. The resource itself stays.
 *
 * ⚠️ **A cell is hours PER HEAD** (the contract's `hours`); the row's hours and cost multiply by
 * the headcount, and a cell of a several-head resource says so in its title.
 *
 * ⚠️ **Edits are drafts until saved** (`state-rules.md` §8 — blur does not save). Every changed
 * cell batches into one `PUT /cells`; Enter saves the batch, Escape drops that cell's draft. A line
 * item's name and revenue are the same: Enter saves, Escape reverts. The footer moves with the
 * drafts, so the figure being typed is the one summed.
 *
 * ⚠️ **A licence line carries no resources** (the mockup's *Licenc sortételre nem kerül
 * erőforrás*) — unless it somehow holds hours (its type changed after planning), in which case they
 * are drawn, so no hour in the scenario is ever invisible.
 *
 * ⚠️ **A month's revenue in the footer is placed at the line item's end month**, or the last month
 * it holds hours when it has no dates; the totals column is `scenarioFigures`, exactly.
 */

const P = 'P';
const MAX_HOURS = 2000;
type CellKey = string;
const cellKey = (lineId: string, resourceId: string, col: string): CellKey =>
  `${lineId}|${resourceId}|${col}`;
const pairKey = (lineId: string, resourceId: string): string => `${lineId}|${resourceId}`;
const firstOf = (date: string): string => `${date.slice(0, 7)}-01`;

const ROW_GROUP =
  'bg-[color-mix(in_oklch,var(--muted)_30%,var(--background))] hover:bg-[color-mix(in_oklch,var(--muted)_30%,var(--background))]';
const INLINE =
  'hover:ring-input focus:ring-ring/50 h-7 w-full rounded-sm bg-transparent px-1.5 outline-none hover:ring-1 focus:ring-2 read-only:hover:ring-0';

type Pending =
  | { readonly kind: 'line'; readonly line: ScenarioLine }
  | { readonly kind: 'resource'; readonly resource: ScenarioResource };

export function ScenarioGrid({
  detail,
  withOptions,
  readOnly,
  send,
  profiles,
  rateCurrency,
}: {
  readonly detail: DealScenarioDetail;
  readonly withOptions: boolean;
  readonly readOnly: boolean;
  readonly send: ScenarioSend;
  readonly profiles: readonly PlaceholderProfile[];
  /** The workspace currency — what the profiles' and resources' cost rates are in. */
  readonly rateCurrency: string;
}) {
  const t = useT();
  const { fmt, fmtNum, fmtShort, parse } = useDuration();
  const { locale } = useI18n();
  const currency = detail.currencyCode;
  const recurring = detail.kind === 'recurring';

  const [drafts, setDrafts] = useState<ReadonlyMap<CellKey, string>>(new Map());
  const [lineDrafts, setLineDrafts] = useState<
    ReadonlyMap<string, { readonly name?: string; readonly amount?: string }>
  >(new Map());
  const [attached, setAttached] = useState<ReadonlySet<string>>(new Set());
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [extra, setExtra] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [resourceDialog, setResourceDialog] = useState<{
    resource: ScenarioResource | null;
    lineId: string | null;
  } | null>(null);

  /* ── what the server holds ── */
  const baseInput = useMemo(() => scenarioInput(detail, withOptions), [detail, withOptions]);
  const relevant = useMemo(
    () => detail.cells.filter((cell) => (recurring ? cell.period === null : cell.period !== null)),
    [detail.cells, recurring],
  );
  const saved = useMemo(() => {
    const map = new Map<CellKey, number>();
    for (const cell of relevant) {
      map.set(cellKey(cell.lineId, cell.resourceId, cell.period ?? P), Number(cell.hours));
    }
    return map;
  }, [relevant]);
  const pairs = useMemo(() => {
    const set = new Set(attached);
    for (const cell of relevant) set.add(pairKey(cell.lineId, cell.resourceId));
    return set;
  }, [attached, relevant]);

  /* ── the columns ── */
  const cols = useMemo(() => {
    if (recurring) return [P];
    const months = scenarioMonths(baseInput);
    const last = months.at(-1) ?? firstOf(detail.startsOn);
    return [...months, ...Array.from({ length: extra }, (_, at) => addMonthsWire(last, at + 1))];
  }, [recurring, baseInput, detail.startsOn, extra]);
  const periodOf = (col: string): string | null => (col === P ? null : col);

  /** A cell's figure: its valid draft, else what is saved. Per head. */
  const hoursAt = (key: CellKey): number => {
    const draft = drafts.get(key);
    if (draft !== undefined) {
      const parsed = draft.trim() === '' ? 0 : parse(draft);
      if (parsed !== null) return parsed;
    }
    return saved.get(key) ?? 0;
  };

  /* ── the figures, drafts included ── */
  const figures = useMemo(() => {
    if (drafts.size === 0) return scenarioFigures(baseInput);
    const merged = new Map(saved);
    for (const [key, text] of drafts) {
      const parsed = text.trim() === '' ? 0 : parse(text);
      if (parsed !== null) merged.set(key, parsed);
    }
    const others = baseInput.cells.filter((cell) =>
      recurring ? cell.period !== null : cell.period === null,
    );
    const cells = [...merged].map(([key, hours]) => {
      const [lineId = '', resourceId = '', col = P] = key.split('|');
      return { lineId, resourceId, period: col === P ? null : col, hours };
    });
    return scenarioFigures({ ...baseInput, cells: [...others, ...cells] });
  }, [baseInput, drafts, saved, recurring, parse]);

  const lines = [...detail.lines].sort((a, b) => a.sortOrder - b.sortOrder);
  const resources = [...detail.resources].sort((a, b) => a.sortOrder - b.sortOrder);
  const counts = (line: ScenarioLine): boolean => withOptions || line.type !== 'option';
  const rowsOf = (line: ScenarioLine): ScenarioResource[] =>
    resources.filter((resource) => pairs.has(pairKey(line.id, resource.id)));

  const lineHoursAt = (line: ScenarioLine, col: string): number =>
    rowsOf(line).reduce(
      (sum, resource) => sum + hoursAt(cellKey(line.id, resource.id, col)) * resource.headcount,
      0,
    );
  const lineCostAt = (line: ScenarioLine, col: string): number =>
    rowsOf(line).reduce(
      (sum, resource) =>
        sum +
        hoursAt(cellKey(line.id, resource.id, col)) *
          resource.headcount *
          Number(resource.costRate),
      0,
    );
  const placement = (line: ScenarioLine): string | null => {
    if (recurring) return P;
    if (line.endsOn !== null) return firstOf(line.endsOn);
    const worked = cols.filter((col) => lineHoursAt(line, col) > 0);
    return worked.at(-1) ?? null;
  };
  const counted = lines.filter(counts);
  const colHours = cols.map((col) => counted.reduce((sum, l) => sum + lineHoursAt(l, col), 0));
  const colCost = cols.map((col) => counted.reduce((sum, l) => sum + lineCostAt(l, col), 0));
  const colRevenue = cols.map((col) =>
    counted
      .filter((line) => placement(line) === col)
      .reduce((sum, line) => sum + Number(line.amount), 0),
  );

  /* ── writes ── */
  const saveDrafts = async (): Promise<void> => {
    if (drafts.size === 0 || busy) return;
    const cells: { lineId: string; resourceId: string; period: string | null; hours: number }[] =
      [];
    for (const [key, text] of drafts) {
      const hours = text.trim() === '' ? 0 : parse(text);
      if (hours === null) {
        setError(t('dealScenario.error.hours'));
        return;
      }
      if (hours > MAX_HOURS) {
        setError(t('dealScenario.error.maxHours'));
        return;
      }
      const [lineId = '', resourceId = '', col = P] = key.split('|');
      if ((saved.get(key) ?? 0) === hours) continue;
      cells.push({ lineId, resourceId, period: periodOf(col), hours });
    }
    if (cells.length === 0) {
      setDrafts(new Map());
      return;
    }
    setBusy(true);
    setError(null);
    let refusal: string | null = null;
    /* The contract takes 500 cells a request. */
    for (let at = 0; at < cells.length && refusal === null; at += 500) {
      const answer = await send('/cells', 'PUT', { cells: cells.slice(at, at + 500) });
      if (typeof answer === 'string') refusal = answer;
    }
    setBusy(false);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    setDrafts(new Map());
  };

  const saveLine = async (
    line: ScenarioLine,
    patch: { name?: string; type?: DealLineItemType; amount?: number },
  ): Promise<boolean> => {
    setError(null);
    const answer = await send(`/lines/${line.id}`, 'PUT', {
      name: patch.name ?? line.name,
      type: patch.type ?? line.type,
      amount: patch.amount ?? Number(line.amount),
      startsOn: line.startsOn,
      endsOn: line.endsOn,
      version: line.version,
    });
    if (typeof answer === 'string') {
      setError(answer);
      return false;
    }
    return true;
  };

  const dropLineDraft = (lineId: string, field: 'name' | 'amount'): void => {
    setLineDrafts((current) => {
      const next = new Map(current);
      const { [field]: _dropped, ...rest } = current.get(lineId) ?? {};
      if (Object.keys(rest).length === 0) next.delete(lineId);
      else next.set(lineId, rest);
      return next;
    });
  };
  const setLineDraft = (lineId: string, field: 'name' | 'amount', value: string): void => {
    setError(null);
    setLineDrafts((current) =>
      new Map(current).set(lineId, { ...current.get(lineId), [field]: value }),
    );
  };

  const commitName = async (line: ScenarioLine): Promise<void> => {
    const draft = lineDrafts.get(line.id)?.name;
    if (draft === undefined) return;
    if (draft.trim() === '') {
      setError(t('dealScenario.error.lineName'));
      return;
    }
    if (draft.trim() === line.name || (await saveLine(line, { name: draft.trim() }))) {
      dropLineDraft(line.id, 'name');
    }
  };
  const commitAmount = async (line: ScenarioLine): Promise<void> => {
    const draft = lineDrafts.get(line.id)?.amount;
    if (draft === undefined) return;
    const value = parseAmount(draft);
    if (value === null) {
      setError(t('deals.error.value'));
      return;
    }
    if (value === Number(line.amount) || (await saveLine(line, { amount: value }))) {
      dropLineDraft(line.id, 'amount');
    }
  };

  const attach = (lineId: string, resourceId: string): void => {
    setAttached((current) => new Set(current).add(pairKey(lineId, resourceId)));
    setCollapsed((current) => {
      const next = new Set(current);
      next.delete(lineId);
      return next;
    });
  };

  const fromProfile = async (lineId: string, profileId: string): Promise<void> => {
    setError(null);
    const before = new Set(detail.resources.map((one) => one.id));
    const answer = await send('/resources', 'POST', { profileId });
    if (typeof answer === 'string') {
      setError(answer);
      return;
    }
    const made = answer?.resources.find((one) => !before.has(one.id));
    if (made !== undefined) attach(lineId, made.id);
  };

  /** Zero every saved cell of one line × resource pairing. */
  const clearPair = async (lineId: string, resourceId: string): Promise<string | null> => {
    const cells = relevant
      .filter((cell) => cell.lineId === lineId && cell.resourceId === resourceId)
      .map((cell) => ({ lineId, resourceId, period: cell.period, hours: 0 }));
    setDrafts((current) => {
      const next = new Map(current);
      for (const key of current.keys()) {
        if (key.startsWith(`${pairKey(lineId, resourceId)}|`)) next.delete(key);
      }
      return next;
    });
    if (cells.length === 0) return null;
    const answer = await send('/cells', 'PUT', { cells });
    return typeof answer === 'string' ? answer : null;
  };

  const detach = async (lineId: string, resourceId: string): Promise<void> => {
    const refusal = await clearPair(lineId, resourceId);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    setAttached((current) => {
      const next = new Set(current);
      next.delete(pairKey(lineId, resourceId));
      return next;
    });
  };

  const duplicateLine = async (line: ScenarioLine): Promise<void> => {
    setError(null);
    const before = new Set(detail.lines.map((one) => one.id));
    const answer = await send('/lines', 'POST', {
      name: t('dealScenario.copyName', { name: line.name }).slice(0, 200),
      type: line.type,
      amount: Number(line.amount),
      startsOn: line.startsOn,
      endsOn: line.endsOn,
    });
    if (typeof answer === 'string') {
      setError(answer);
      return;
    }
    const made = answer?.lines.find((one) => !before.has(one.id));
    if (made === undefined) return;
    const cells = relevant
      .filter((cell) => cell.lineId === line.id)
      .map((cell) => ({
        lineId: made.id,
        resourceId: cell.resourceId,
        period: cell.period,
        hours: Number(cell.hours),
      }));
    for (const resource of rowsOf(line)) attach(made.id, resource.id);
    if (cells.length === 0) return;
    const copied = await send('/cells', 'PUT', { cells });
    if (typeof copied === 'string') setError(copied);
  };

  const setHeadcount = async (resource: ScenarioResource, headcount: number): Promise<void> => {
    setError(null);
    const answer = await send(`/resources/${resource.id}`, 'PATCH', {
      headcount,
      version: resource.version,
    });
    if (typeof answer === 'string') setError(answer);
  };

  const confirmPending = async (): Promise<void> => {
    if (pending === null) return;
    const answer =
      pending.kind === 'line'
        ? await send(`/lines/${pending.line.id}`, 'DELETE')
        : await send(`/resources/${pending.resource.id}`, 'DELETE');
    if (typeof answer === 'string') {
      setPendingError(answer);
      return;
    }
    setPending(null);
  };

  /* ── menus ── */
  const lineMenu = (line: ScenarioLine, open: boolean, canHold: boolean): MenuItem[] => [
    ...(readOnly
      ? []
      : [
          {
            label: t('dealScenario.line.duplicate'),
            icon: 'copy' as const,
            onSelect: () => void duplicateLine(line),
          },
        ]),
    {
      label: open ? t('dealScenario.line.collapse') : t('dealScenario.line.expand'),
      icon: 'users',
      disabled: !canHold,
      onSelect: () => {
        setCollapsed((current) => {
          const next = new Set(current);
          if (open) next.add(line.id);
          else next.delete(line.id);
          return next;
        });
      },
    },
    ...(readOnly
      ? []
      : [
          { type: 'separator' as const },
          {
            label: t('dealScenario.line.delete'),
            icon: 'trash' as const,
            destructive: true,
            onSelect: () => {
              setPendingError(null);
              setPending({ kind: 'line', line });
            },
          },
        ]),
  ];

  const resourceMenu = (line: ScenarioLine, resource: ScenarioResource): MenuItem[] => [
    { type: 'label', label: t('dealScenario.resource.headcount') },
    ...[1, 2, 3].map((count) => ({
      label: t('dealScenario.resource.heads', { count }),
      checked: resource.headcount === count,
      onSelect: () => void setHeadcount(resource, count),
    })),
    {
      label: t('dealScenario.resource.editAction'),
      icon: 'pencil',
      onSelect: () => {
        setResourceDialog({ resource, lineId: null });
      },
    },
    { type: 'separator' },
    {
      label: t('dealScenario.resource.clear'),
      icon: 'x',
      onSelect: () => {
        void clearPair(line.id, resource.id).then((refusal) => {
          if (refusal !== null) setError(refusal);
        });
      },
    },
    {
      label: t('dealScenario.resource.detach'),
      icon: 'x',
      onSelect: () => void detach(line.id, resource.id),
    },
    { type: 'separator' },
    {
      label: t('dealScenario.resource.delete'),
      icon: 'trash',
      destructive: true,
      onSelect: () => {
        setPendingError(null);
        setPending({ kind: 'resource', resource });
      },
    },
  ];

  const addMenu = (line: ScenarioLine): MenuItem[] => {
    const shown = new Set(rowsOf(line).map((one) => one.id));
    const existing = resources.filter((one) => !shown.has(one.id));
    return [
      ...(existing.length === 0
        ? []
        : [
            { type: 'label' as const, label: t('dealScenario.add.existing') },
            ...existing.map((resource) => ({
              label: resource.name,
              hint: t('dealScenario.resource.rate', {
                rate: dealMoney(resource.costRate, rateCurrency),
              }),
              dot: resource.color ?? 'var(--muted-foreground)',
              onSelect: () => {
                attach(line.id, resource.id);
              },
            })),
            { type: 'separator' as const },
          ]),
      { type: 'label' as const, label: t('dealScenario.add.profiles') },
      ...(profiles.length === 0
        ? [{ label: t('dealScenario.add.noProfiles'), disabled: true }]
        : profiles.map((profile) => ({
            label: `${profile.projectRoleName} · ${seniorityLabel(t, profile.seniority)}`,
            hint: t('dealScenario.resource.rate', {
              rate: dealMoney(profile.costRate, rateCurrency),
            }),
            dot: profile.projectRoleColor,
            onSelect: () => void fromProfile(line.id, profile.id),
          }))),
      { type: 'separator' as const },
      {
        label: t('dealScenario.add.free'),
        icon: 'user-round' as const,
        onSelect: () => {
          setResourceDialog({ resource: null, lineId: line.id });
        },
      },
    ];
  };

  /* ── drawing ── */
  const figure = (hours: number): string => (hours === 0 ? '' : fmtShort(hours));
  /* An input's draft is the bare figure, so it re-parses in the reader's unit (DU-1). */
  const draftFigure = (hours: number): string => (hours === 0 ? '' : fmtNum(hours));
  const colHead = (col: string): ReactNode => {
    if (col === P) {
      return (
        <>
          <div className="text-foreground text-[11.5px] tracking-normal normal-case">
            {t('dealScenario.grid.period')}
          </div>
          <div className="text-[10.5px] uppercase">{t('dealScenario.grid.template')}</div>
        </>
      );
    }
    const parts = monthParts(col, locale);
    return (
      <>
        <div className="text-foreground text-[11.5px] tracking-normal normal-case">{parts.top}</div>
        <div className="text-[10.5px] tabular-nums">{parts.year}</div>
      </>
    );
  };
  const colLabel = (col: string): string =>
    col === P ? t('dealScenario.grid.period') : monthLabel(col, locale);
  const tail = recurring || readOnly ? null : <TableCell className="w-10" />;

  return (
    <div className="flex min-w-0 flex-col">
      {error !== null || drafts.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
          <div className="flex-1" />
          {error === null ? null : (
            <span className="text-destructive text-[12px]" role="alert">
              {error}
            </span>
          )}
          {drafts.size === 0 ? null : (
            <>
              <span className="text-muted-foreground text-[12px] tabular-nums">
                {t('dealScenario.drafts', { count: drafts.size })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDrafts(new Map());
                  setError(null);
                }}
              >
                {t('dealScenario.discard')}
              </Button>
              <Button size="sm" disabled={busy} onClick={() => void saveDrafts()}>
                {t('common.save')}
              </Button>
            </>
          )}
        </div>
      ) : null}

      <Table label={t('dealScenario.grid.label')} className="w-max min-w-full">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="bg-background sticky left-0 z-10 min-w-[300px] pl-4">
              {t('dealScenario.grid.item')}
            </TableHead>
            <TableHead className="min-w-[124px]">{t('dealScenario.grid.type')}</TableHead>
            <TableHead className="min-w-[84px] text-right">
              {t('dealScenario.grid.hours')}
            </TableHead>
            <TableHead className="min-w-[132px] text-right">
              {t('dealScenario.grid.revenue')}
            </TableHead>
            <TableHead className="min-w-[116px] text-right">
              {t('dealScenario.grid.cost')}
            </TableHead>
            <TableHead className="min-w-[76px] text-right">
              {t('dealScenario.grid.margin')}
            </TableHead>
            {cols.map((col) => (
              <TableHead
                key={col}
                className={cn(
                  'h-auto min-w-[110px] py-1.5 text-right',
                  col === P && 'bg-primary/5',
                )}
              >
                {colHead(col)}
              </TableHead>
            ))}
            {recurring || readOnly ? null : (
              <TableHead className="w-10 px-1">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('dealScenario.grid.addMonth')}
                  title={t('dealScenario.grid.addMonth')}
                  onClick={() => {
                    setExtra((count) => count + 1);
                  }}
                >
                  <Icon name="plus" size={14} />
                </Button>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => {
            const rows = rowsOf(line);
            const licence = line.type === 'license';
            const canHold = !licence || rows.length > 0;
            const open = canHold && !collapsed.has(line.id);
            const hours = cols.reduce((sum, col) => sum + lineHoursAt(line, col), 0);
            const cost = cols.reduce((sum, col) => sum + lineCostAt(line, col), 0);
            const amount = Number(line.amount);
            const margin = amount > 0 ? (amount - cost) / amount : null;
            const isCounted = counts(line);
            const draft = lineDrafts.get(line.id);
            return (
              <Fragment key={line.id}>
                <TableRow className={ROW_GROUP}>
                  <TableCell className="sticky left-0 z-[1] bg-inherit py-1 pl-2">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        disabled={!canHold}
                        aria-expanded={open}
                        aria-label={
                          open ? t('dealScenario.line.collapse') : t('dealScenario.line.expand')
                        }
                        onClick={() => {
                          setCollapsed((current) => {
                            const next = new Set(current);
                            if (open) next.add(line.id);
                            else next.delete(line.id);
                            return next;
                          });
                        }}
                      >
                        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
                      </Button>
                      <input
                        aria-label={t('dealScenario.line.name')}
                        readOnly={readOnly}
                        value={draft?.name ?? line.name}
                        onChange={(event) => {
                          setLineDraft(line.id, 'name', event.target.value);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void commitName(line);
                          } else if (event.key === 'Escape') {
                            dropLineDraft(line.id, 'name');
                          }
                        }}
                        className={cn(
                          INLINE,
                          'min-w-0 flex-1 text-[12.5px] font-medium',
                          draft?.name !== undefined && 'bg-accent/60',
                        )}
                      />
                      <DropdownMenu
                        label={t('dealScenario.line.menu')}
                        align="end"
                        items={lineMenu(line, open, canHold)}
                      >
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('dealScenario.line.menu')}
                        >
                          <Icon name="ellipsis" size={14} />
                        </Button>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                  <TableCell className="px-1 py-1">
                    <Select
                      className="h-7 text-[12px]"
                      value={line.type}
                      disabled={readOnly}
                      aria-label={t('dealScenario.line.type', { name: line.name })}
                      onChange={(event) => {
                        void saveLine(line, { type: event.target.value as DealLineItemType });
                      }}
                      options={DEAL_LINE_ITEM_TYPES.map((type) => ({
                        value: type,
                        label: lineTypeLabel(t, type),
                      }))}
                    />
                  </TableCell>
                  <TableCell
                    numeric
                    className={cn(!isCounted && 'text-muted-foreground line-through')}
                    title={isCounted ? undefined : t('dealScenario.line.optionOff')}
                  >
                    {licence && hours === 0 ? '–' : fmtShort(hours)}
                  </TableCell>
                  <TableCell className="px-1 py-1">
                    <input
                      aria-label={t('dealScenario.line.amount', { name: line.name })}
                      readOnly={readOnly}
                      inputMode="decimal"
                      value={draft?.amount ?? amountField(amount, locale)}
                      onChange={(event) => {
                        setLineDraft(line.id, 'amount', event.target.value);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          void commitAmount(line);
                        } else if (event.key === 'Escape') {
                          dropLineDraft(line.id, 'amount');
                        }
                      }}
                      className={cn(
                        INLINE,
                        'text-right text-[12.5px] tabular-nums',
                        draft?.amount !== undefined && 'bg-accent/60',
                        !isCounted && 'text-muted-foreground',
                      )}
                    />
                  </TableCell>
                  <TableCell numeric className="text-muted-foreground">
                    {cost === 0 ? '–' : dealMoney(cost, currency)}
                  </TableCell>
                  <TableCell
                    numeric
                    className={cn(
                      margin !== null && margin < 0 ? 'text-destructive' : 'text-muted-foreground',
                    )}
                  >
                    {pctText(margin, locale)}
                  </TableCell>
                  {cols.map((col) => (
                    <TableCell
                      key={col}
                      numeric
                      className={cn('font-medium', col === P && 'bg-primary/5')}
                    >
                      {figure(lineHoursAt(line, col))}
                    </TableCell>
                  ))}
                  {tail}
                </TableRow>

                {open
                  ? rows.map((resource) => {
                      const rowHours = cols.reduce(
                        (sum, col) =>
                          sum + hoursAt(cellKey(line.id, resource.id, col)) * resource.headcount,
                        0,
                      );
                      const color = resource.color ?? 'var(--muted-foreground)';
                      return (
                        <TableRow key={resource.id}>
                          <TableCell className="bg-background sticky left-0 z-[1] py-1 pl-10">
                            <div className="flex items-center gap-2">
                              <span
                                aria-hidden
                                className="grid size-5 shrink-0 place-items-center rounded-full border border-dashed"
                                style={{ borderColor: color }}
                              >
                                <span
                                  className="size-1.5 rounded-full"
                                  style={{ background: color }}
                                />
                              </span>
                              <span className="min-w-0 flex-1 leading-tight">
                                <span className="block truncate text-[12px] font-medium">
                                  {resource.headcount > 1
                                    ? `${resource.name} · ${t('dealScenario.resource.heads', {
                                        count: resource.headcount,
                                      })}`
                                    : resource.name}
                                </span>
                                <span className="text-muted-foreground block truncate text-[11px] tabular-nums">
                                  {t('dealScenario.resource.rate', {
                                    rate: dealMoney(resource.costRate, rateCurrency),
                                  })}
                                </span>
                              </span>
                              {readOnly ? null : (
                                <DropdownMenu
                                  label={t('dealScenario.resource.menu')}
                                  align="end"
                                  items={resourceMenu(line, resource)}
                                >
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={t('dealScenario.resource.menu')}
                                  >
                                    <Icon name="ellipsis" size={14} />
                                  </Button>
                                </DropdownMenu>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[12px]">
                            {resource.seniority === null
                              ? ''
                              : seniorityLabel(t, resource.seniority)}
                          </TableCell>
                          <TableCell numeric>{rowHours === 0 ? '–' : fmtShort(rowHours)}</TableCell>
                          <TableCell />
                          <TableCell numeric className="text-muted-foreground">
                            {rowHours === 0
                              ? '–'
                              : dealMoney(rowHours * Number(resource.costRate), currency)}
                          </TableCell>
                          <TableCell />
                          {cols.map((col) => {
                            const key = cellKey(line.id, resource.id, col);
                            const cellDraft = drafts.get(key);
                            const stored = saved.get(key) ?? 0;
                            const label = t('dealScenario.cell', {
                              name: resource.name,
                              line: line.name,
                              period: colLabel(col),
                            });
                            const title =
                              resource.headcount > 1 && stored > 0
                                ? t('dealScenario.cell.heads', {
                                    hours: fmt(stored),
                                    count: resource.headcount,
                                  })
                                : undefined;
                            if (readOnly) {
                              return (
                                <TableCell
                                  key={col}
                                  numeric
                                  aria-label={label}
                                  title={title}
                                  className={cn(col === P && 'bg-primary/5')}
                                >
                                  {figure(stored)}
                                </TableCell>
                              );
                            }
                            const invalid =
                              cellDraft !== undefined &&
                              cellDraft.trim() !== '' &&
                              (parse(cellDraft) ?? MAX_HOURS + 1) > MAX_HOURS;
                            return (
                              <TableCell
                                key={col}
                                className={cn('px-1 py-1', col === P && 'bg-primary/5')}
                              >
                                <Tooltip asChild content={title}>
                                  <input
                                    aria-label={label}
                                    aria-invalid={invalid || undefined}
                                    inputMode="decimal"
                                    value={cellDraft ?? draftFigure(stored)}
                                    onChange={(event) => {
                                      const value = event.target.value;
                                      setError(null);
                                      setDrafts((current) => new Map(current).set(key, value));
                                    }}
                                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        void saveDrafts();
                                      } else if (event.key === 'Escape') {
                                        setDrafts((current) => {
                                          const next = new Map(current);
                                          next.delete(key);
                                          return next;
                                        });
                                      }
                                    }}
                                    className={cn(
                                      INLINE,
                                      'text-right text-[12.5px] tabular-nums',
                                      cellDraft !== undefined && 'bg-accent/60',
                                      invalid && 'ring-destructive ring-1',
                                    )}
                                  />
                                </Tooltip>
                              </TableCell>
                            );
                          })}
                          {tail}
                        </TableRow>
                      );
                    })
                  : null}

                {open && !readOnly && !licence ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell className="bg-background sticky left-0 z-[1] py-1 pl-9">
                      <div className="flex items-center gap-2">
                        <DropdownMenu
                          label={t('dealScenario.add.to', { name: line.name })}
                          items={addMenu(line)}
                          width={300}
                          searchPlaceholder={t('dealScenario.add.search')}
                          emptyLabel={t('dealScenario.add.empty')}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t('dealScenario.add.to', { name: line.name })}
                          >
                            <Icon name="plus" size={14} />
                            {t('dealScenario.add.resource')}
                          </Button>
                        </DropdownMenu>
                        {rows.length === 0 ? (
                          <span className="text-muted-foreground truncate text-[11.5px]">
                            {t('dealScenario.add.none')}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell colSpan={cols.length + (tail === null ? 5 : 6)} />
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}

          {/* ── the footer: hours, labour, revenue, margin ── */}
          <TableRow className="border-t-2 hover:bg-transparent">
            <TableCell className="bg-background sticky left-0 z-[1] pl-4 text-[12px] font-medium">
              {t('dealScenario.foot.hours')}
            </TableCell>
            <TableCell />
            <TableCell numeric className="font-medium">
              {fmtShort(figures.hours)}
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            {colHours.map((hours, index) => (
              <TableCell key={cols[index]} numeric className="font-medium">
                {figure(hours)}
              </TableCell>
            ))}
            {tail}
          </TableRow>
          <TableRow className="hover:bg-transparent">
            <TableCell className="bg-background sticky left-0 z-[1] pl-4 text-[12px] font-medium">
              {t('dealScenario.foot.labour')}
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell numeric className="font-medium">
              {dealMoney(figures.labour, currency)}
            </TableCell>
            <TableCell />
            {colCost.map((cost, index) => (
              <TableCell key={cols[index]} numeric className="text-muted-foreground">
                {cost === 0 ? '' : dealMoneyShort(cost, currency)}
              </TableCell>
            ))}
            {tail}
          </TableRow>
          <TableRow className="hover:bg-transparent">
            <TableCell className="bg-background sticky left-0 z-[1] pl-4 text-[12px] font-medium">
              {t('dealScenario.foot.revenue')}
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell numeric className="font-medium">
              {dealMoney(figures.revenue, currency)}
            </TableCell>
            <TableCell />
            <TableCell />
            {colRevenue.map((revenue, index) => (
              <TableCell key={cols[index]} numeric className="font-medium">
                {revenue === 0 ? '' : dealMoneyShort(revenue, currency)}
              </TableCell>
            ))}
            {tail}
          </TableRow>
          <TableRow className="border-t-2 hover:bg-transparent">
            <TableCell className="bg-background sticky left-0 z-[1] pl-4 text-[12px] font-medium">
              {t('dealScenario.foot.margin')}
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell
              numeric
              className={cn('font-medium', figures.margin < 0 && 'text-destructive')}
            >
              {pctText(figures.marginPct, locale)}
            </TableCell>
            {cols.map((col, index) => {
              const revenue = colRevenue[index] ?? 0;
              const cost = colCost[index] ?? 0;
              const margin = revenue - cost;
              return (
                <TableCell key={col} numeric className={cn(margin < 0 && 'text-destructive')}>
                  {revenue === 0 && cost === 0 ? '' : dealMoneyShort(margin, currency)}
                </TableCell>
              );
            })}
            {tail}
          </TableRow>
        </TableBody>
      </Table>

      <ScenarioResourceDialog
        open={resourceDialog !== null}
        resource={resourceDialog?.resource ?? null}
        existing={detail.resources.map((one) => one.id)}
        send={send}
        onClose={() => {
          setResourceDialog(null);
        }}
        onCreated={(id) => {
          if (resourceDialog?.lineId != null) attach(resourceDialog.lineId, id);
        }}
      />
      <PlanConfirm
        open={pending !== null}
        title={
          pending?.kind === 'resource'
            ? t('dealScenario.confirm.resourceTitle')
            : t('dealScenario.confirm.lineTitle')
        }
        body={
          pending?.kind === 'resource'
            ? t('dealScenario.confirm.resourceBody', { name: pending.resource.name })
            : t('dealScenario.confirm.lineBody', {
                name: pending?.kind === 'line' ? pending.line.name : '',
              })
        }
        confirmLabel={t('common.delete')}
        error={pendingError}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={() => void confirmPending()}
      />
    </div>
  );
}
